import React, { useContext, useState, useRef } from "react";
import { executeActions, getWidgetSpecificIsVisible } from "@utils/index";
import { connect } from "react-redux";
import { useOnload } from "@utils/useOnLoad";
import { Context } from "@components/useContext/context";
import ImageContainer from "@components/imageContainer";
import { resolvePath } from "@utils/resolvePath";
import { CommonProps } from "@utils/interface";
import CloneChildren from "@components/children";
import {
  SuperContainer,
  Container,
  Header,
  Logo,
  HeaderText,
  CarouselContainer,
  CarouselWrapper,
  CarCard,
  CarImageContainer,
  CarDetails,
  CarName,
  LocationText,
  PriceContainer,
  Price,
  SavingsText,
  NavigationDots,
  Dot,
  ExploreButton,
  GetACallBackButton
} from "./styles";
import Image from "next/image";
import { convertToLakh } from "@utils/inrFormatter";

interface ICarData {
  id: string;
  name: string;
  image: string;
  location: string;
  price: string;
  savings: string;
}

interface IAckoDriveCarousel extends CommonProps {
  title?: string;
  logoSrc: string;
  cars?: Array<ICarData>;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  city?: string;
  carDetails?: Array<any>;
  styles?: object;
  mStyles?: object;
}

const mapStateToProps = function (state: any, ownProps: any) {
  return {
    is_visible: getWidgetSpecificIsVisible(
      state.configuration?.normalisedConfig,
      ownProps.id
    )
  };
};

const AckoDriveCarousel = ({ children, ...props }: IAckoDriveCarousel) => {
  const {
    is_visible,
    actions,
    title,
    logoSrc,
    carDetails = [],
    primaryButtonText,
    secondaryButtonText,
    city,
    styles,
    mStyles
  } = props;

  const {
    on_load,
    on_click,
    on_close,
    on_primary_button_click,
    on_secondary_button_click
  } = actions || {};
  const { isDesktop } = useContext(Context);
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  useOnload({ on_load });
  const formatSavings = (savings: number) => {
    return `₹${Math.round(savings)?.toLocaleString("en-US")} less than dealer price`;
  };

  const handleExploreClick = () => {
    executeActions(on_primary_button_click);
  };
  const handleGetACallBackClick = () => {
    executeActions(on_secondary_button_click);
  };
  const handleCloseClick = () => {
    executeActions(on_close);
  };

  const handleDotClick = (index: number) => {
    setCurrentSlide(index);
    if (carouselRef.current) {
      if (isDesktop) {
        // Desktop: Fixed card width 320px + gap
        const cardWidth = 320;
        const gap = 20;
        carouselRef.current.scrollTo({
          left: index * (cardWidth + gap),
          behavior: "smooth"
        });
      } else {
        // Mobile: Scroll by container width
        const containerWidth = carouselRef.current.clientWidth;
        carouselRef.current.scrollTo({
          left: index * containerWidth,
          behavior: "smooth"
        });
      }
    }
  };

  const handleScroll = () => {
    if (carouselRef.current) {
      if (isDesktop) {
        // Desktop: Fixed card width 320px + gap
        const cardWidth = 320;
        const gap = 20;
        const scrollLeft = carouselRef.current.scrollLeft;
        const newIndex = Math.round(scrollLeft / (cardWidth + gap));
        setCurrentSlide(Math.min(newIndex, carDetails.length - 1));
      } else {
        const cardWidth = 250;
        const gap = 20;
        const scrollLeft = carouselRef.current.scrollLeft;
        const newIndex = Math.round(scrollLeft / (cardWidth + gap));
        setCurrentSlide(Math.min(newIndex, carDetails.length - 1));
      }
    }
  };

  if (!is_visible) {
    return null;
  }

  const currentCar = carDetails[currentSlide] || carDetails[0];

  return (
    <SuperContainer>
      <Container styles={styles} mStyles={mStyles}>
        <Header>
          <Logo>
            <ImageContainer
              imgSrc={resolvePath(logoSrc)}
              alt="ACKO Drive"
              width={isDesktop ? 140 : 120}
              height={isDesktop ? 40 : 32}
              styles={{}}
            />
            <div
              style={{ position: "absolute", top: 0, right: 16 }}
              onClick={handleCloseClick}
            >
              <ImageContainer
                imgSrc={resolvePath("/images/close-icon.svg")}
                alt="close"
                width={24}
                height={24}
              />
            </div>
          </Logo>
          <HeaderText>{title}</HeaderText>
        </Header>

        <CarouselContainer>
          <CarouselWrapper ref={carouselRef} onScroll={handleScroll}>
            {carDetails.map((car, index) => (
              <CarCard key={`${car.brand_id}-${car.model_id}`}>
                <CarImageContainer
                  backgroundImage={resolvePath(
                    `/images/acko_drive-bg-${(index % 5) + 1}.svg`
                  )}
                >
                  <img
                    src={
                      car.heroImageUrl ||
                      resolvePath("/images/sampleCarImage.svg")
                    }
                    alt={car.model_name}
                    style={{
                      height: "200px",
                      width: "234px",
                      objectFit: "contain",
                      objectPosition: "center",
                      position: "relative",
                      zIndex: 1
                    }}
                  />
                </CarImageContainer>

                <CarDetails>
                  <CarName>
                    {car.brand_name} {car.model_name}
                  </CarName>
                  {city && city !== "" && (
                    <LocationText>
                      On-road price in {city} starts from
                    </LocationText>
                  )}
                  <PriceContainer>
                    <Price>₹{convertToLakh(car.min_price)} lakh</Price>
                    {"  "}
                    <SavingsText>
                      {formatSavings(car.min_acko_drive_savings)}
                    </SavingsText>
                  </PriceContainer>
                </CarDetails>
              </CarCard>
            ))}
          </CarouselWrapper>
        </CarouselContainer>

        {carDetails.length > 1 && !isDesktop && (
          <NavigationDots>
            {carDetails.map((_, index) => (
              <Dot
                key={index}
                active={index === currentSlide}
                onClick={() => handleDotClick(index)}
              />
            ))}
          </NavigationDots>
        )}

        <ExploreButton onClick={handleExploreClick}>
          {primaryButtonText}
        </ExploreButton>

        <GetACallBackButton onClick={handleGetACallBackClick}>
          {secondaryButtonText}
        </GetACallBackButton>

        <CloneChildren
          children={children}
          props={props}
          styles={styles}
          mStyles={mStyles}
        />
      </Container>
    </SuperContainer>
  );
};

export default connect(mapStateToProps)(AckoDriveCarousel);
